require('./Extensions')

/**
 * Process
 */
class SignType {
    /**
     * Constuctor
     * @param {string} id
     * @param {string} label
     * @param {string} pdfLabel
     * @param {number} smallSizeCost
     * @param {number} mediumSizeCost
     * @param {number} largeSizeCost
     * @param {number} orderIndex
     */
    constructor(id=null, label='',
        pdfLabel=null,
        smallSizeCost=0.0, mediumSizeCost=0.0, largeSizeCost=0.0,
        orderIndex=1000) {
        this.id = id
        this.label = label
        this.pdf_label = pdfLabel
        this.small_size_cost = smallSizeCost
        this.medium_size_cost = mediumSizeCost
        this.large_size_cost = largeSizeCost
        this.order_index = orderIndex
    }

    /**
     * @param {json} json
     * @return {SignType}
     */
    static from(json) {
        return Object.assign(new SignType(), json)
    }

    /**
     * Returns the label up to the word date
     * @return {string}
     */
    labelNoDate() {
        const match = this.label.match(/Date [0-9\/]+$/)
        if (match) {
            return this.label.substring(0, match.index).trim()
        }
        return this.label
    }

    /**
     * Return the pdf label
     * @param {string} defaultValue ; Undefinded = this.pdf_label
     * @return {string} pdf label
     */
    getPDFLabel(defaultValue) {
        if (this.pdf_label === null) {
            if (defaultValue !== undefined) {
                return defaultValue
            }
            return this.label
        }
        return this.pdf_label
    }
}

/**
 * AddonsOptions
 */
class AddonOptionItem {
    /**
     * @param {string} label
     * @param {string} pdfLabel
     * @param {number} squareInchPrice
     * @param {number} unitPrice
     */
    constructor(label='', pdfLabel='', squareInchPrice=0.00, unitPrice=0.00) {
        this.label = label
        this.pdf_label = pdfLabel
        this.square_inch_price = squareInchPrice
        this.unit_price = unitPrice
    }

    /**
     * Creates a new AddonOption from json
     * @param {json} json
     * @return {AddonOption}
     */
    static from(json) {
        const item = Object.assign(new AddonOptionItem(), json)
        item.square_inch_price = Number.parseFloat(item.square_inch_price)
        item.unit_price = Number.parseFloat(item.unit_price)
        return item
    }
}

/**
 * AddonWithOptions
 */
class AddonWithOptions {
    /**
     * @param {string} id
     * @param {string} costLabel
     * @param {[[AddonOption]]} options
     * @param {number} orderIndex
     * @param {number} costOrderIndex
     * @param {boolean} inuse
     */
    constructor(
        id='',
        costLabel='',
        options=[],
        orderIndex=0,
        costOrderIndex=0,
        inuse=true,
    ) {
        this.id = id
        this.cost_label = costLabel
        this.options = options
        this.order_index = orderIndex
        this.cost_order_index = costOrderIndex
        this.inuse = inuse
    }

    /**
     * Creates a new AddonWithOptions from json
     * @param {json} json
     * @return {AddonWithOptions}
     */
    static from(json) {
        const addon = Object.assign(new AddonWithOptions(), json)
        for (let i = 0; i < addon.options.length; i++) {
            for (let j = 0; j < addon.options[i].length; j++) {
                addon.options[i][j] = AddonOptionItem.from(addon.options[i][j])
            }
        }
        return addon
    }

    /**
     * Adds an option item to the option at index specified
     * @param {number} index
     */
    addOptionItem(index=0) {
        this.options[index].push(new AddonOptionItem())
    }

    /**
     * Adds an option
     * @param {number} index
     */
    addOption() {
        this.options.push([new AddonOptionItem()])
    }

    /**
     * Removes an item at [optionIndex][itemIndex]
     *      if optionIndex group
     *      is now empty it will remove the gorup
     * @param {number} optionIndex
     * @param {number} itemIndex
     */
    removeOptionItem(optionIndex=0, itemIndex=0) {
        this.options[optionIndex] =
            this.options[optionIndex].removeAtIndex(itemIndex)
        if (this.options[optionIndex].length === 0) {
            this.options = this.options.removeAtIndex(optionIndex)
        }
    }

    /**
     * @param {[Number]} picks
     * @return {[AddonOptionItem]}
     */
    toItems(picks) {
        const addons = []
        for (let i = 0; i < this.options.length; i++) {
            addons.push(this.options[i][picks[i]])
        }
        return addons
    }

    /**
     * @return {string}
     */
    labelForZeroPicks() {
        if (this.options.length == 0) return ''
        return this.options
            .map((x) => x[0] ? x[0].label : '')
            .reduce((l, r) => l + ' ' + r)
    }

    /**
     * @param {[Number]} picks
     * @return {SignAddon}
     */
    toSignAddon(picks) {
        while (picks.length < this.options.length) {
            picks.push(0)
        }

        const addon = this.toItems(picks).reduce((l, r) => {
            l.label += r.label
            l.pdf_label += r.pdf_label
            l.square_inch_price += r.square_inch_price
            l.unit_price += r.unit_price

            return l
        }, new SignAddon())

        addon.cost_label = this.cost_label
        addon.id = this.id

        return addon
    }

    /**
     * @param {SignAddon} addon
     * @param {boolean} verifyPrice
     * @return {[Number]?}
     */
    getPicksOfAddon(addon, verifyPrice) {
        /**
         * @param {AddonWithOptions} ctx
         * @param {number} index
         * @param {string} currentLabel
         * @return {number} index; -1 iff none found
         */
        function getPicks(ctx, index, currentLabel) {
            const indexes = []
            for (let j = 0; j < ctx.options[index].length; j++) {
                const oLabel = ctx.options[index][j].label
                if (currentLabel.substr(0, oLabel.length) === oLabel) {
                    indexes.push(j)
                }
            }
            return indexes
        }

        /**
         * Builds up the addon by doing recursive calls
         * @param {context} ctx which should be this
         * @param {number} index should start as 0
         * @param {[number]} picks the current picks so far
         * @param {text} label left
         * @param {number} unitPrice current unit price building up
         * @param {number} squareInchPrice current unit price building up
         * @return {{label,unitPrice,squareInchPrice}?}
         */
        function buildUp(ctx, index=0, picks=[], label='', unitPrice=0.0,
            squareInchPrice=0.0) {
            if (index >= ctx.options.length) {
                if (label === '') {
                    return {label, picks, unitPrice, squareInchPrice}
                }
                return null
            }

            const newPicks = getPicks(ctx, index, label)

            for (let i = 0; i < newPicks.length; i++) {
                picks.push(newPicks[i])
                const r = buildUp(ctx, index+1,
                    picks,
                    label.substr(ctx.options[index][newPicks[i]].label.length),
                    unitPrice + ctx.options[index][newPicks[i]].unit_price,
                    squareInchPrice +
                        ctx.options[index][newPicks[i]].square_inch_price,
                )
                if (r !== null) {
                    return r
                }
                picks = picks.slice(0, picks.length-1)
            }

            return null
        }

        const r = buildUp(this, 0, [], addon.label)
        if (r === null) {
            return null
        }
        const {unitPrice, picks, squareInchPrice} = r

        if (verifyPrice &&
            (addon.unit_price !== unitPrice ||
            addon.square_inch_price !== squareInchPrice)) {
            return null
        }

        return picks
    }

    /**
     * @param {SignAddon} addon
     * @return {[Number]?}
     */
    toUpgradeAddon(addon) {
        const picks = this.getPicksOfAddon(addon, false)
        const items = this.toItems(picks)
        let newAddon = new SignAddon()
        newAddon.id = addon.id
        newAddon.cost_label += addon.cost_label
        for (let i = 0; i < items.length; i++) {
            newAddon.label += items[i].label
            newAddon.pdf_label += items[i].pdf_label
            newAddon.square_inch_price += items[i].square_inch_price
            newAddon.unit_price += items[i].unit_price
        }

        return newAddon
    }
}


/**
 * SignAddon
 */
class SignAddon {
    /**
     * Constructor
     * @param {string} id
     * @param {string} costLabel
     * @param {string} label
     * @param {string} pdfLabel
     * @param {number} squareInchPrice
     * @param {number} unitPrice
     */
    constructor(
        id='', costLabel='', label='',
        pdfLabel='',
        squareInchPrice=0.00, unitPrice=0.00,
    ) {
        this.id = id
        this.cost_label = costLabel
        this.label = label
        this.pdf_label = label
        this.square_inch_price = squareInchPrice
        this.unit_price = unitPrice
    }

    /**
     * Creates a new CustomAddonItem from json
     * @param {json} json
     * @return {CustomAddonItem}
     */
    static from(json) {
        const newAddon = Object.assign(new SignAddon(), json)

        if (json.pdf_label === undefined) {
            newAddon.pdf_label = newAddon.label
        }

        return newAddon
    }

    /**
     * @param {AddonWithOptions} addonOptions
     * @return {boolean} true iff (ids or label equal)
     *      but something else does not
     */
    isUpgradeable(addonOptions) {
        return addonOptions.getPicksOfAddon(this, false) !== null &&
            addonOptions.getPicksOfAddon(this, true) === null
    }

    /**
     * @param {json} idToListAddons
     * @param {json} labelToListAddons
     * @return {bool} true iff (ids or label equal)
     *      but something else does not
     */
    hasUpgradeable(idToListAddons, labelToListAddons) {
        const list = [idToListAddons[this.id], labelToListAddons[this.label[0]]]
        for (let i = 0; i < list.length; i++) {
            if (!list[i]) {
                continue
            }

            for (let j = 0; j < list[i].length; j++) {
                if (this.isUpgradeable(list[i][j])) {
                    return true
                }
            }
        }

        return false
    }

    /**
     * Used addonOptions
     * @param {json} idToListAddons
     * @param {json} labelToListAddons
     */
    setUpgradeable(idToListAddons, labelToListAddons) {
        const list = [idToListAddons[this.id], labelToListAddons[this.label[0]]]
        for (let i = 0; i < list.length; i++) {
            if (list[i] === undefined) {
                continue
            }

            for (let j = 0; j < list[i].length; j++) {
                if (this.isUpgradeable(list[i][j])) {
                    this.set(list[i][j].toUpgradeAddon(this))
                    return
                }
            }
        }
    }

    /**
     * Sets the values to this
     * @param {SignAddon} otherAddon
     */
    set(otherAddon) {
        this.id = otherAddon.id
        this.cost_label = otherAddon.cost_label
        this.label = otherAddon.label
        this.pdf_label = otherAddon.pdf_label
        this.square_inch_price = otherAddon.square_inch_price
        this.unit_price = otherAddon.unit_price
    }

    /**
     * @param {SignAddon} otherAddon
     * @return {boolean} true iff all properties including id are equal
     */
    equals(otherAddon) {
        return this.id === otherAddon.id &&
            this.cost_label === otherAddon.cost_label &&
            this.label === otherAddon.label &&
            this.pdf_label === otherAddon.pdf_label &&
            this.square_inch_price === otherAddon.square_inch_price &&
            this.unit_price === otherAddon.unit_price
    }

    /**
     * Used for selecting addons in quote page
     * @param {SignAddon} otherAddon
     * @return {boolean} true iff all properties including id are equal
     */
    lightEquals(otherAddon) {
        return this.cost_label === otherAddon.cost_label &&
            this.label === otherAddon.label &&
            this.square_inch_price === otherAddon.square_inch_price &&
            this.unit_price === otherAddon.unit_price
    }

    /**
     * Coverts a SignAddon to an addon with options
     *      but will have only 1 option.
     * @return {AddonWithOptions}
     */
    toAddonWithOptions() {
        const options = AddonWithOptions.from({
            'id': this.id,
            'cost_label': this.cost_label,
            'options': [[
                {
                    'label': this.label,
                    'pdf_label': this.pdf_label,
                    'square_inch_price': this.square_inch_price,
                    'unit_price': this.unit_price,
                },
            ]],
            'order_index': this.order_index,
        })
        return options
    }
}

/**
 * CustomAddonItem is if you want to build squareInchPrice
 */
class CustomAddonItem {
    /**
     * Constructor
     * @param {string} label
     * @param {number} squareInchPriceNoSize
     * @param {number} squareInchPrice
     * @param {number} unitPrice
     */
    constructor(label='', squareInchPriceNoSize=0.00,
        squareInchPrice=0.00, unitPrice=0.00) {
        this.label = label
        this.square_inch_price_no_size = squareInchPriceNoSize
        this.square_inch_price = Number.parseFloat(squareInchPrice)
        this.unit_price = Number.parseFloat(unitPrice)
    }

    /**
     * Creates a new CustomAddonItem from json
     * @param {json} json
     * @return {CustomAddonItem}
     */
    static from(json) {
        return Object.assign(new CustomAddonItem(), json)
    }

    /**
     * @return {CustomAddonItem}
     */
    copy() {
        return CustomAddonItem.from(this)
    }
}

/**
 * CustomAddon is for a custom addon
 */
class CustomAddon {
    /**
     * constructor
     * @param {number} percentMarkedUp
     * @param {boolean} overrideZeroQuantity
     * @param {[CustomAddonItem]} customAddons
     */
    constructor(percentMarkedUp=0.00,
        overrideZeroQuantity=false,
        customAddons=[new CustomAddonItem()]) {
        this.percent_marked_up = percentMarkedUp
        this.override_zero_quantity = overrideZeroQuantity
        this.custom_addons = customAddons
    }

    /**
     * Create a CustomAddon from json
     * @param {json} json
     * @return {CustomAddon}
     */
    static from(json) {
        const addons = Object.assign(new CustomAddon(), json)

        // If an old sign that has not been updated yet
        if (addons.square_inch_item_list &&
            !addons.updated_to_jan_2022
        ) {
            let unitPrice = 0.0
            let squareInchPrice = 0.0

            for (let i = 0; i < addons.square_inch_item_list.length; i++) {
                const addon = new CustomAddonItem(
                    addons.square_inch_item_list[i].label,
                    0.00,
                    addons.square_inch_item_list[i].value,
                    0.00,
                )
                squareInchPrice += addon.square_inch_price
                addons.custom_addons.push(addon)
            }

            for (let i = 0; i < addons.unit_item_list.length; i++) {
                const addon = new CustomAddonItem(
                    addons.unit_item_list[i].label,
                    0.00,
                    0.00,
                    addons.unit_item_list[i].value,
                )
                unitPrice += addon.unit_price
                addons.custom_addons.push(addon)
            }

            if (addons.square_inch_price !== squareInchPrice ||
                addons.unit_price !== unitPrice ||
                addons.description.length > 0
            ) {
                const addon = new CustomAddonItem(
                    addons.description,
                    0.00,
                    addons.square_inch_price-squareInchPrice,
                    addons.unit_price-unitPrice,
                )
                addons.custom_addons.push(addon)
            }

            addons.updated_to_jan_2022 = true
        } else if (typeof addons.unit_price !== 'undefined' &&
            !addons.updated_to_jan_2022) {
            // old old sign with no square_inch_item_list
            const addon = new CustomAddonItem(
                addons.description,
                0.00,
                addons.square_inch_price,
                addons.unit_price,
            )
            addons.custom_addons.push(addon)
            addons.updated_to_jan_2022 = true
        }

        for (let i = 0; i < addons.custom_addons.length; i++) {
            addons.custom_addons[i] =
                CustomAddonItem.from(addons.custom_addons[i])
        }

        if (addons.custom_addons.length > 1 &&
            addons.custom_addons[0].label.length === 0 &&
            addons.custom_addons[0].square_inch_price === 0.0 &&
            addons.custom_addons[0].unit_price === 0.0
        ) {
            addons.custom_addons = addons.custom_addons.splice(1)
        }

        return addons
    }

    /**
     * @return {CustomAddon} a copy of the custom addons
     */
    copy() {
        const addon = CustomAddon.from(this)
        addon.custom_addons = []
        for (let i = 0; i < this.custom_addons.length; i++) {
            addon.custom_addons.push(this.custom_addons[i].copy())
        }
        return addon
    }

    /**
     * Adds another custom addon
     */
    addAddon() {
        this.custom_addons.push(new CustomAddonItem())
    }

    /**
     * @param {number} index
     */
    removeAddon(index) {
        this.custom_addons = this.custom_addons.removeAtIndex(index)
    }

    /**
     * @return {string} the description
     */
    getDescription() {
        const addonLabels = []

        for (let i = 0; i < this.custom_addons.length; i++) {
            addonLabels.push(this.custom_addons[i].label)
        }

        return addonLabels.join(', ')
    }

    /**
     * @param {number/string} price
     */
    setSquareInchPrice(price) {
        // alert('This function does not work anymore. setSquareInchPrice')
        // this.square_inch_price = price
    }

    /**
     * @param {number/string} price
     */
    setUnitPrice(price) {
        // alert('This function does not work anymore. setUnitPrice')
        // this.unit_price = price
    }

    /**
     * This will parse the price from string to
     *      number
     */
    parsePrice() {
        // alert('This function does not work anymore. parsePrice')
        // this.square_inch_price = parseFloat(this.square_inch_price)
        // this.unit_price = parseFloat(this.unit_price)
        // this.percent_marked_up = parseFloat(this.percent_marked_up)
    }

    /**
     * Return the total square inch price for each custom addon
     * @return {number}
     */
    getTotalSquareInchPrice() {
        return this.custom_addons.sum((x) =>
            Number.parseFloat(x.square_inch_price))
    }

    /**
     * Return the total unit price for each custom addon
     * @return {number}
     */
    getTotalUnitPrice() {
        return this.custom_addons.sum((x) => Number.parseFloat(x.unit_price))
    }

    /**
     * Note this does not include square_inch_price_no_size
     * Returns the squareInch price.
     *      If you dont specify squareInch then
     *      it will return a string
     * @param {number} squareInch
     * @return {number/string}
     */
    getSquareInchPrice(squareInch) {
        if (squareInch) {
            // this.parsePrice()
            return this.getTotalSquareInchPrice() +
                this.getTotalUnitPrice() / squareInch
        }
        // alert('This function does not work anymore. getSquareInchPrice')
        // return this.getTotalSquareInchPrice()
        return this.getTotalSquareInchPrice()
    }

    /**
     * Returns sum of addons.square_inch_price_no_size
     * @return {number}
     */
    getSquareInchPriceNoSize() {
        return this.custom_addons.sum((x) =>
            Number.parseFloat(x.square_inch_price_no_size))
    }

    /**
     * Note this does not include square_inch_price_no_size
     * Returns the squareInch price.
     *      If you dont specify squareInch then
     *      it will return a string
     * @param {number} squareInch
     * @return {number/string}
     */
    getUnitPrice(squareInch) {
        if (squareInch) {
            return squareInch * this.getSquareInchPrice(squareInch)
        }
        // alert('This function does not work anymore. getUnitPrice')
        // return this.unit_price
        return this.getTotalUnitPrice()
    }
}


/**
 * Sign
 */
class Sign {
    /**
     * @param {string} id
     * @param {string} givenId
     * @param {CustomAddon} customAddons
     * @param {SignType} type
     * @param {[SignAddon]} addons
     * @param {number} height
     * @param {number} width
     * @param {number} quantity
     * @param {number} orderIndex
     * @param {[SignAddon]} subsigns
     */
    constructor(id='', givenId='', customAddons=new CustomAddon(),
        type=(new SignType()), addons=[],
        height='0.0', width='0.0', quantity='0',
        orderIndex=0,
        subsigns=[],
    ) {
        this.id = id
        this.given_id = givenId
        this.custom_addons = customAddons
        this.type = type
        this.addons = addons
        this.height = height
        this.width = width
        this.quantity = quantity
        this.order_index = orderIndex
        this.subsigns = subsigns
    }

    /**
     * @param {json} json
     * @return {Sign}
     */
    static from(json) {
        const sign = Object.assign(new Sign(), json)
        sign.custom_addons = CustomAddon.from(sign.custom_addons)
        sign.type = SignType.from(sign.type)
        for (let i = 0; i < sign.addons.length; i++) {
            sign.addons[i] = SignAddon.from(sign.addons[i])
        }

        for (let i = 0; i < sign.subsigns.length; i++) {
            sign.subsigns[i] = Sign.from(sign.subsigns[i])
            delete sign.subsigns[i].subsigns
            delete sign.subsigns[i].picks
        }

        return sign
    }

    /**
     * @return {Sign}
     */
    copy() {
        const sign = Sign.from(JSON.parse(JSON.stringify(this)))
        sign.id = null
        sign.addons = this.addons.slice()
        sign.custom_addons = sign.custom_addons.copy()
        return sign
    }

    /**
     * It copies only stuff needed on the quote page
     * @return {CustomAddon}
     */
    copyRemoveAddons() {
        const sign = Sign.from(JSON.parse(JSON.stringify(this)))
        sign.id = null
        sign.given_id = ''
        sign.addons = []
        sign.subsigns = []
        sign.custom_addons = new CustomAddon()
        sign.width = ''
        sign.height = ''
        sign.quantity = ''
        return sign
    }

    /**
     * @return {bool} is process other
     */
    isTypeOther() {
        return this.type.label === 'Other'
    }

    /**
     * @param {AddonWithOptions} addon
     * @param {boolean} verifyPrice
     * @return {[Number]?} picks
     */
    getPicksOfAddon(addon, verifyPrice=true) {
        for (let i = 0; i < this.addons.length; i++) {
            const picks = addon.getPicksOfAddon(this.addons[i], verifyPrice)
            if (picks !== null) {
                return picks
            }
        }
        return null
    }

    /**
     * @param {[AddonWithOptions]} addons
     * @return {{idToListAddons, labelToListAddons}}
     *      idToListAddons [id of AddonWithOptions / SignAddon]
     *               -> [AddonWithOptions]
            labelToListAddons [first character in label]
     *               -> [AddonWithOptions]
     */
    buildListAndIdToAddonWithOptions(addons) {
        const idToListAddons = {}
        const labelToListAddons = {}
        for (let i = 0; i < addons.length; i++) {
            if (!idToListAddons[addons[i].id]) {
                idToListAddons[addons[i].id] = []
            }
            idToListAddons[addons[i].id].push(addons[i])


            if (addons[i].options.length > 0) {
                for (let j = 0; j < addons[i].options[0].length; j++) {
                    const firstChar = (addons[i].options[0][j].label + ' ')[0]
                    if (!labelToListAddons[firstChar]) {
                        labelToListAddons[firstChar] = []
                    }
                    labelToListAddons[firstChar].push(addons[i])
                }
            }
        }
        return {idToListAddons, labelToListAddons}
    }

    /**
     * @param {[AddonWithOptions]} addons
     * @return {bool} iff addons can be updated
     */
    hasAddonThatCanBeUpgraded(addons) {
        const {idToListAddons, labelToListAddons} =
            this.buildListAndIdToAddonWithOptions(addons)
        for (let i = 0; i < this.addons.length; i++) {
            if (this.addons[i].hasUpgradeable(
                idToListAddons, labelToListAddons)) {
                return true
            }
        }

        return false
    }

    /**
     * @param {[AddonWithOptions]} addons
     */
    upgradeToNewestAddons(addons) {
        const idToListAddons = {}
        const labelToListAddons = {}
        for (let i = 0; i < addons.length; i++) {
            if (!idToListAddons[addons[i].id]) {
                idToListAddons[addons[i].id] = []
            }
            if (!labelToListAddons[addons[i].label]) {
                labelToListAddons[addons[i].label] = []
            }
            idToListAddons[addons[i].id].push(addons[i])
            labelToListAddons[addons[i].label].push(addons[i])
        }
        for (let i = 0; i < this.addons.length; i++) {
            this.addons[i].setUpgradeable(idToListAddons, labelToListAddons)
        }
    }

    /**
     * @param {Sign} addon
     */
    appendSubsigns(addon = new Sign()) {
        delete addon.subsigns
        this.subsigns.push(addon)
    }

    /**
     * Sorts all addons
     * @param {json} allAddons type_id->[sorted AddonWithOptions]
     */
    sortAddons(allAddons) {
        this.addons = this.addons.map((addon, index) => {
            addon.order_index = allAddons[this.type.id]
                .findIndex((addonOption) => {
                    return addonOption.getPicksOfAddon(addon) !== null
                })
            return addon
        })

        this.addons = this.addons.sort((lhs, rhs) => {
            if (lhs.order_index === rhs.order_index) {
                // should return 0 as its the same addon
                if (lhs.square_inch_price !== rhs.square_inch_price) {
                    return lhs.square_inch_price - rhs.square_inch_price
                }
                return lhs.unit_price - rhs.unit_price
            }

            if (lhs.order_index === -1) {
                return 1
            }
            if (rhs.order_index === -1) {
                return -1
            }

            return lhs.order_index - rhs.order_index
        })

        this.addons = this.addons.map((addon, index) => {
            delete addon.order_index
            return addon
        })
    }

    /**
     * Empties the addons
     */
    resetAddons() {
        this.addons = []
        this.custom_addons = new CustomAddon()
    }

    /**
     * Sets the fieldName to equal value
     * @param {string} fieldName
     * @param {some} value
     */
    setField(fieldName, value) {
        if (fieldName === 'type') {
            // We only reset when switching between diferent sign types
            //      etc old raster braile to new doesnt count
            // this.resetAddons()
        }

        this[fieldName] = value
    }

    /**
     * @param {SignAddon} addon
     * @return {bool}
     */
    hasSignAddon(addon) {
        return this.addons.find((x) =>
            x.lightEquals(addon),
            // x.equals(addon),
        ) !== undefined
    }

    /**
     * @param {AddonWithOptions} addon
     * @return {bool}
     */
    hasAddonWithOptions(addon) {
        return this.getPicksOfAddon(addon) !== null
    }

    /**
     * Removes all addons with addon.id
     * @param {SignAddon} addon
     */
    removeAddon(addon) {
        this.addons = this.addons.filter((v) => !v.lightEquals(addon))
    }

    /**
     * @param {SignAddon} addon
     */
    toggleAddon(addon) {
        const newAdds = this.addons.filter((v) => !v.lightEquals(addon))
        if (newAdds.length === this.addons.length) {
            this.addons.push(addon)
        } else {
            this.addons = newAdds
        }
    }

    /**
     * @return {number}
     */
    squareInches() {
        const sqrinch = this.height * this.width
        if (sqrinch === 0) {
            return 1.0
        }
        return sqrinch
    }

    /**
     * Loosing any square inches should not change the price
     * @return {number} smallest square inch price
     */
    minimumSquareInchSize() {
        return this.type.small_size_cost / this.type.medium_size_cost
    }

    /**
     * @return {number}
     */
    getSquareInchIndex() {
        const squareInches = this.squareInches()
        if (squareInches < this.minimumSquareInchSize()) {
            return 0
        } else if (squareInches <= 63) {
            return 1
        } else {
            return 2
        }
    }

    /**
     * Returns the price without addons per square inch
     * @param {number} index optional
     * @return {number}
     */
    basicPricePerSquareInch(index=null) {
        if (index === null) {
            index = this.getSquareInchIndex()
        }

        if (index === 0) {
            return this.type.small_size_cost / this.squareInches()
        } else if (index === 1) {
            return this.type.medium_size_cost
        } else {
            return this.type.large_size_cost
        }
    }

    /**
     * Calculates the non custom addons and returns
     *      them in square inch price
     * @return {number}
     */
    addonsCostPerSquareInch() {
        let price = 0.0
        let unitPrice = 0.0
        for (let i = 0; i < this.addons.length; i++) {
            price += this.addons[i].square_inch_price
            unitPrice += this.addons[i].unit_price
        }

        // this.custom_addons.parsePrice()
        price += this.custom_addons.getSquareInchPrice()
        unitPrice += this.custom_addons.getUnitPrice()
        const customSquareInchNoSize =
            this.custom_addons.getSquareInchPriceNoSize()

        if (this.getSquareInchIndex() === 0) {
            return (price * this.minimumSquareInchSize() / this.squareInches() +
                unitPrice / this.squareInches()) + customSquareInchNoSize
        }

        const sqrInches = this.squareInches()
        if (sqrInches < 24 && this.isTypeOther()) {
            return ((price * 24/sqrInches) + unitPrice / sqrInches) +
                customSquareInchNoSize
        }

        return (price + unitPrice / sqrInches) + customSquareInchNoSize
    }

    /**
     * Calculates the square inch price
     * @return {number}
     */
    sqrInchPrice() {
        return (Number.parseFloat(this.custom_addons.percent_marked_up) + 1.0) *
                (this.basicPricePerSquareInch() +
                    this.addonsCostPerSquareInch())
    }


    /**
     * Calculates the unit price
     * It rounds to the nearest hundredth
     * @return {number}
     */
    unitPrice() {
        const price = this.sqrInchPrice() * this.squareInches()
        return Math.round(price * 100 + 0.0000000001) / 100
    }

    /**
     * Calculates the total price
     * @return {number}
     */
    totalPrice() {
        return this.unitPrice() * this.quantity
    }

    /**
     * Calculates the total price + subsigns total price
     * @return {number}
     */
    totalPriceIncludingSubsigns() {
        let totalPrice = this.totalPrice()
        for (let i = 0; i < (this.subsigns ? this.subsigns.length : 0); i++) {
            totalPrice += this.subsigns[i].totalPriceIncludingSubsigns() *
                this.quantity
        }
        return totalPrice
    }

    /**
     * @return {number} total price including subsigns / quantity
     */
    unitPriceIncludingSubsigns() {
        if (this.quantity == 0) {
            return this.unitPrice() + (this.subsigns ?
                this.subsigns.sum((x) => x.unitPrice()) : 0)
        }
        return this.totalPriceIncludingSubsigns() / this.quantity
    }

    /**
     * @return {number} unit price including subsigns / quantity
     */
    squareInchPriceIncludingSubsigns() {
        return this.unitPriceIncludingSubsigns() /
            (1.0 * this.height * this.width)
    }

    /**
     * Return the type pdf label
     * @return {string} pdf label
     */
    getTypePDFLabel() {
        return this.type.getPDFLabel()
    }
}

module.exports = {
    Sign,
    SignType,
    SignAddon,
    AddonWithOptions,
    CustomAddon,
    CustomAddonItem,
}

